import React, { useMemo } from 'react';
import * as R from 'ramda';
import { useIntl } from '@atlassian/jira-intl';
import type { OptionType } from '@atlassian/jira-portfolio-3-common/src/select/types';
import { PRODUCT_ANALYTICS_EVENT_NAMES } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics/types';
import { NO_SPRINT_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/dependencies';
import type { Sprint } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/sprints';
import SearchablePopupSelect from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/dependencies/popup-select/searchable';
import commonMessages from '@atlassian/jira-portfolio-3-portfolio/src/common/view/messages';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import CustomizedOption from './customized-option';
import messages from './messages';
import { type Props, STATE_CATEGORY } from './types';

function SprintFilter({
	externalSprints,
	sprintsAndTeams: { sprintsWithTeams, sprintsWithoutTeams },
	sprintFilter,
	setSprintFilter,
	onFilterChange,
	sprintStatesCategories,
	isOpen = false,
}: Props) {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const getOptionBySprint = (sprint: Sprint) => ({
		label: sprint.title,
		value: sprint.id.toString(),
		state: sprint.state,
	});

	const noSprintOption = useMemo(
		() => ({
			type: NO_SPRINT_ID,
			label: intl.formatMessage(commonMessages.noSprintLabel),
			value: NO_SPRINT_ID,
		}),
		[intl],
	);

	const { options, allSprint } = useMemo(() => {
		// eslint-disable-next-line @typescript-eslint/no-shadow
		const allSprint = new Set<Sprint>();
		// eslint-disable-next-line @typescript-eslint/no-shadow
		const options: OptionType[] = [noSprintOption];
		if (sprintStatesCategories.length > 0) {
			sprintStatesCategories.forEach(({ checkboxState, sprintIds, sprintState }) => {
				options.push({
					type: STATE_CATEGORY,
					sprintState,
					sprintIds,
					checkboxState,
				});
			});
		}

		const populateOptions = (sprintsInGroup: Sprint[], groupTitle: string) => {
			const groupOptions: OptionType[] = [];
			sprintsInGroup.forEach((sprint: Sprint) => {
				const option = getOptionBySprint(sprint);
				groupOptions.push(option);
				allSprint.add(sprint);
			});
			options.push({
				label: groupTitle,
				options: groupOptions,
			});
		};

		sprintsWithTeams.forEach((sprintsInGroup) => {
			populateOptions(sprintsInGroup.sprints, sprintsInGroup.teams.join(', '));
		});
		if (sprintsWithoutTeams.length) {
			populateOptions(sprintsWithoutTeams, intl.formatMessage(commonMessages.noAssociatedTeams));
		}
		if (externalSprints.length) {
			populateOptions(externalSprints, intl.formatMessage(commonMessages.otherSprints));
		}
		return { options, allSprint };
	}, [
		intl,
		externalSprints,
		sprintsWithTeams,
		sprintsWithoutTeams,
		sprintStatesCategories,
		noSprintOption,
	]);

	const values: OptionType[] | null = useMemo(() => {
		if (!sprintFilter.size) {
			return null;
		}
		// eslint-disable-next-line @typescript-eslint/no-shadow
		const values: OptionType[] = [];
		const allSprintById = R.indexBy<Sprint>(R.prop('id'), Array.from<Sprint>(allSprint));
		sprintFilter.forEach((sprintId) => {
			if (sprintId === NO_SPRINT_ID) {
				values.push(noSprintOption);
			} else if (allSprintById[sprintId]) {
				values.push(getOptionBySprint(allSprintById[sprintId]));
			}
		});
		return values;
	}, [sprintFilter, allSprint, noSprintOption]);

	const filterOption = (option: OptionType, rawInput: string): boolean => {
		if (option.data.type === STATE_CATEGORY || option.data.type === NO_SPRINT_ID) {
			return !rawInput.length;
		}
		if (option.label === undefined) return false;
		const query = rawInput.toLowerCase();
		return option.label.toString().toLowerCase().includes(query);
	};

	return (
		<SearchablePopupSelect
			label={intl.formatMessage(commonMessages.sprint)}
			isMulti
			isOpen={isOpen}
			value={values}
			searchPlaceholder={intl.formatMessage(messages.placeholder)}
			options={options}
			customizeComponents={
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				{
					Option: CustomizedOption,
				} as Record<string, Function>
			}
			closeMenuOnSelect={false}
			hideSelectedOptions={false}
			onChange={(changedOptions) => {
				if (changedOptions?.length) {
					setSprintFilter(changedOptions.map((option: OptionType) => option.value));
				} else {
					setSprintFilter([]);
				}
				onFilterChange();

				const [actionSubject, action] =
					PRODUCT_ANALYTICS_EVENT_NAMES.UPDATED_DEPENDENCY_SPRINT_FILTER.split(' ');
				fireUIAnalytics(
					createAnalyticsEvent({
						action,
						actionSubject,
					}),
					{
						sprintsCount: changedOptions ? changedOptions.length : 0,
					},
				);
			}}
			filterOption={filterOption}
			testId="portfolio-3-portfolio.app-simple-plans.main.tabs.dependencies.filters.sprint"
		/>
	);
}

export default SprintFilter;
